export const WORKFLOWS = [
  {
    slug: "high-level-ot",
    code: "WF_OT_001",
    number: "11.1",
    title: "High-level Workflow of Operation Theatre",
    category: "Peri-Operative",
    summary:
      "End-to-end flow covering all major and minor surgical procedures performed in the OT unit.",
    steps: [
      { title: "Surgery indicated", actor: "Treating Doctor", description: "Patient evaluated and surgery is recommended based on clinical findings." },
      { title: "Consent & counseling", actor: "Surgeon / Nurse", description: "Informed consent taken, risks explained, attendant briefed." },
      { title: "Pre-anesthetic check-up", actor: "Anesthetist", description: "Fitness assessment, optimization of comorbidities, ASA grading." },
      { title: "OT scheduling", actor: "OT Coordinator", description: "Slot booked based on case priority, surgeon and OT availability." },
      { title: "Intra-operative care", actor: "OT Team", description: "Anesthesia, surgical procedure and continuous monitoring." },
      { title: "Recovery & PACU", actor: "Recovery Nurse", description: "Patient shifted to PACU, vitals stabilized before ward transfer." },
      { title: "Post-operative care", actor: "Ward Team", description: "Pain management, wound care and rehabilitation begin." },
    ],
  },
  {
    slug: "emergency-scheduling",
    code: "WF_OT_002",
    number: "11.2",
    title: "Scheduling of Emergency Cases",
    category: "Scheduling",
    summary:
      "Rapid pathway for life-threatening cases requiring resuscitation, stabilization and immediate surgery.",
    steps: [
      { title: "Emergency triage", actor: "ER Doctor", description: "Patient triaged as red-category surgical emergency." },
      { title: "Resuscitate & stabilize", actor: "ER Team", description: "Airway, breathing, circulation managed; bloods sent." },
      { title: "Notify OT & on-call surgeon", actor: "ER Coordinator", description: "Emergency OT activated; on-call team paged." },
      { title: "High-risk consent", actor: "Surgeon", description: "Verbal/written consent obtained from patient or kin." },
      { title: "Anesthesia clearance", actor: "Anesthetist", description: "On-the-spot PAC; ASA-E grading documented." },
      { title: "Shift to emergency OT", actor: "OT Porter", description: "Patient wheeled into the emergency theatre with monitoring." },
      { title: "Surgery & handover", actor: "OT Team", description: "Procedure performed, post-op plan and ICU bed arranged." },
    ],
  },
  {
    slug: "daycare-scheduling",
    code: "WF_OT_003",
    number: "11.3",
    title: "Scheduling of Day-care Cases",
    category: "Scheduling",
    summary:
      "Elective procedures performed and discharged within 24 hours of admission.",
    steps: [
      { title: "OPD recommendation", actor: "Surgeon", description: "Patient selected for day-care eligibility." },
      { title: "Day-care booking", actor: "OT Scheduler", description: "Procedure slot booked, instructions shared." },
      { title: "Pre-op investigations", actor: "Lab / Radiology", description: "Baseline workup completed before admission day." },
      { title: "Same-day admission", actor: "Admission Desk", description: "Patient admitted to day-care ward in the morning." },
      { title: "Procedure performed", actor: "OT Team", description: "Short-duration surgery under chosen anesthesia." },
      { title: "Recovery & observation", actor: "Day-care Nurse", description: "Vitals, pain, nausea observed for several hours." },
      { title: "Discharge within 24h", actor: "Treating Doctor", description: "Patient discharged with prescription and follow-up." },
    ],
  },
  {
    slug: "elective-scheduling",
    code: "WF_OT_004",
    number: "11.4",
    title: "Scheduling of Elective Cases",
    category: "Scheduling",
    summary:
      "Planned surgeries scheduled in advance with no medical emergency involved.",
    steps: [
      { title: "Surgery advised", actor: "OPD Surgeon", description: "Elective procedure proposed during OPD consult." },
      { title: "Pre-op workup", actor: "Lab / Radiology", description: "Investigations completed over outpatient visits." },
      { title: "PAC clearance", actor: "Anesthetist", description: "Patient fitness confirmed via pre-anesthesia clinic." },
      { title: "OT slot allocation", actor: "OT Coordinator", description: "Date and slot mutually finalized with patient." },
      { title: "Admission", actor: "Admission Desk", description: "Patient admitted a day prior or as per protocol." },
      { title: "Surgery performed", actor: "OT Team", description: "Planned procedure carried out as scheduled." },
      { title: "Ward shift", actor: "Recovery Nurse", description: "Post-op recovery and ward transfer." },
    ],
  },
  {
    slug: "reschedule-cancellation",
    code: "WF_OT_005",
    number: "11.5",
    title: "Re-Scheduling or Cancellation of Cases",
    category: "Scheduling",
    summary:
      "Triggered when a patient is unfit for surgery or further optimization is needed.",
    steps: [
      { title: "Unfit assessment", actor: "Anesthetist / Surgeon", description: "Patient flagged as unfit or needing optimization." },
      { title: "Reason documented", actor: "OT Coordinator", description: "Cause for postponement captured in OT register." },
      { title: "Inform patient & kin", actor: "Ward Nurse", description: "Communication shared with the family explaining reason." },
      { title: "Cancel/Re-slot", actor: "OT Scheduler", description: "Slot released or new date proposed." },
      { title: "Optimization plan", actor: "Treating Doctor", description: "Further workup or specialist referral arranged." },
      { title: "Re-clearance", actor: "Anesthetist", description: "Repeat PAC after optimization." },
      { title: "Re-confirm booking", actor: "OT Coordinator", description: "New OT date confirmed across teams." },
    ],
  },
  {
    slug: "pre-anesthesia-assessment",
    code: "WF_OT_006",
    number: "11.6",
    title: "Pre-Anesthesia Assessment",
    category: "Assessment",
    summary:
      "Medical and laboratory evaluation by an anesthesia provider to identify peri-operative risk.",
    steps: [
      { title: "Referral to PAC", actor: "Surgeon", description: "Patient referred to pre-anesthesia clinic." },
      { title: "History & examination", actor: "Anesthetist", description: "Detailed medical, drug and airway assessment." },
      { title: "Investigation review", actor: "Anesthetist", description: "Labs, ECG, imaging reviewed; gaps ordered." },
      { title: "Risk stratification", actor: "Anesthetist", description: "ASA grade assigned; comorbidities optimized." },
      { title: "Specialist opinion", actor: "Cardiology / Pulmonology", description: "Cross-consultation if high-risk findings." },
      { title: "Anesthesia plan", actor: "Anesthetist", description: "Technique, monitoring and post-op care planned." },
      { title: "Fitness documented", actor: "Anesthetist", description: "Clearance entered in PAC form and OT list." },
    ],
  },
  {
    slug: "pre-operative-care",
    code: "WF_OT_007",
    number: "11.7",
    title: "Pre-Operative Care",
    category: "Peri-Operative",
    summary:
      "Physical and psychosocial preparation from booking until transfer to the surgical suite.",
    steps: [
      { title: "Booking confirmation", actor: "Ward Nurse", description: "Surgical booking re-verified with consent forms." },
      { title: "NPO & medication review", actor: "Ward Doctor", description: "Fasting orders; routine medication reconciled." },
      { title: "Site marking", actor: "Surgeon", description: "Operative site marked with patient awake." },
      { title: "Pre-op bath & gowning", actor: "Ward Nurse", description: "Antiseptic bath, gown and identification band applied." },
      { title: "Pre-medication", actor: "Ward Nurse", description: "Premedications administered as prescribed." },
      { title: "Checklist completion", actor: "Ward Nurse", description: "WHO pre-op checklist verified before transfer." },
      { title: "Shift to OT", actor: "OT Porter", description: "Patient transported with notes to OT reception." },
    ],
  },
  {
    slug: "intra-operative-care",
    code: "WF_OT_008",
    number: "11.8",
    title: "Intra-Operative Care",
    category: "Peri-Operative",
    summary:
      "From OT admission through anesthesia, surgery and transfer to recovery.",
    steps: [
      { title: "OT reception check-in", actor: "OT Nurse", description: "Identity, consent and site confirmed." },
      { title: "Sign-in (WHO)", actor: "Anesthetist", description: "First WHO Safe Surgery checkpoint completed." },
      { title: "Anesthesia induction", actor: "Anesthetist", description: "Induction and airway secured under monitoring." },
      { title: "Time-out", actor: "OT Team", description: "Team briefing before skin incision." },
      { title: "Surgical procedure", actor: "Surgeon", description: "Procedure performed with continuous monitoring." },
      { title: "Sign-out", actor: "Scrub Nurse", description: "Counts, specimens and concerns reviewed." },
      { title: "Shift to recovery", actor: "Anesthetist", description: "Patient transferred to PACU with handover." },
    ],
  },
  {
    slug: "intra-op-referral",
    code: "WF_OT_009",
    number: "11.9",
    title: "Intra-Operative Interdepartmental Referral",
    category: "Support",
    summary:
      "Cross-specialty opinion or monitoring during surgery when unexpected findings occur.",
    steps: [
      { title: "Unexpected finding", actor: "Operating Surgeon", description: "Complication or finding requires specialist input." },
      { title: "Referral request", actor: "Circulating Nurse", description: "Specialty consult requested via OT desk." },
      { title: "Notify on-call specialist", actor: "OT Coordinator", description: "Concerned department paged urgently." },
      { title: "Sterile entry & review", actor: "Referred Doctor", description: "Specialist scrubs in or advises remotely." },
      { title: "Joint decision", actor: "Surgical Team", description: "Plan modified based on combined opinion." },
      { title: "Documentation", actor: "Scrub Nurse", description: "Referral and decision documented in OT notes." },
      { title: "Resume / extend surgery", actor: "Surgical Team", description: "Procedure continued or extended as required." },
    ],
  },
  {
    slug: "specimen-handling",
    code: "WF_OT_010",
    number: "11.10",
    title: "Handling of Pathological Specimen",
    category: "Support",
    summary:
      "Safe handover and labeling of tissue specimens from the surgical field to the laboratory.",
    steps: [
      { title: "Specimen excised", actor: "Surgeon", description: "Tissue removed and verbally identified." },
      { title: "Receive at scrub", actor: "Scrub Nurse (CST)", description: "Specimen received gently and confirmed." },
      { title: "Handover to circulator", actor: "Circulating Nurse", description: "Specimen passed off safely outside the field." },
      { title: "Container & fixative", actor: "Circulating Nurse", description: "Placed in correct container with fixative if needed." },
      { title: "Labeling", actor: "Circulating Nurse", description: "Patient ID, site and type clearly labelled." },
      { title: "Requisition entry", actor: "OT Clerk", description: "Histopathology request prepared and signed." },
      { title: "Transport to lab", actor: "OT Attendant", description: "Specimen dispatched with chain-of-custody." },
    ],
  },
  {
    slug: "swab-instrument-count",
    code: "WF_OT_011",
    number: "11.11",
    title: "Swab and Instrument Count",
    category: "Support",
    summary:
      "Mandatory counts performed before procedure start and before wound closure.",
    steps: [
      { title: "Initial count", actor: "Scrub & Circulating Nurse", description: "Swabs, sharps and instruments counted before start." },
      { title: "Record on count board", actor: "Circulating Nurse", description: "Counts written on OT count board." },
      { title: "Add-on items tracked", actor: "Scrub Nurse", description: "Any items added during surgery are logged." },
      { title: "Pre-closure count", actor: "Scrub & Circulating Nurse", description: "Second count before cavity closure." },
      { title: "Discrepancy check", actor: "OT Team", description: "If counts mismatch, search and X-ray as needed." },
      { title: "Final count", actor: "Scrub Nurse", description: "Final count after skin closure." },
      { title: "Documentation & sign-off", actor: "Surgeon", description: "Count confirmed correct and signed in record." },
    ],
  },
  {
    slug: "post-operative-care",
    code: "WF_OT_012",
    number: "11.12",
    title: "Post-Operative Care",
    category: "Peri-Operative",
    summary:
      "Care after surgery focused on recovery, pain management and rehabilitation.",
    steps: [
      { title: "PACU admission", actor: "Recovery Nurse", description: "Vitals, airway and pain assessed on arrival." },
      { title: "Stabilization", actor: "Anesthetist", description: "Oxygenation, hemodynamics and consciousness optimized." },
      { title: "Pain management", actor: "Acute Pain Team", description: "Analgesia titrated using protocol." },
      { title: "Ward / ICU transfer", actor: "Recovery Nurse", description: "Patient shifted based on post-op category." },
      { title: "Wound & drain care", actor: "Ward Nurse", description: "Dressing checks, drain output documented." },
      { title: "Mobilization & diet", actor: "Physiotherapy / Nurse", description: "Early ambulation and graded diet started." },
      { title: "Discharge planning", actor: "Treating Doctor", description: "Discharge summary, follow-up and rehab planned." },
    ],
  },
];

export const getWorkflow = (slug) => WORKFLOWS.find((w) => w.slug === slug);
